import React, { useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import HighchartsComponent from "../HighchartsComponent";
import { LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { CustIDByScope, useBrandConfigurator } from "../../contexts/BrandConfiguratorProvider";
import MeasuresContext from "../../contexts/Measures";
import { calculateAvg, medianByMake, mode, weightedAverageByMake } from "../../utils/calculationUtils";
import * as Md from "../../md/full";
import FiltersContext from "../../contexts/Filters";
import { calculationModes, transformSlicesToBrandTree } from "../../routes/BrandModelAnalysis";
import { useAppContext } from "../../contexts/AppContext";

const widgetId = "brand-column-chart";

function ColumnChartAnalysis({ calculationMode = "average", selectedPlayers, showListPrices = true, ...props }) {
    const { filterSelectors } = useBrandConfigurator();
    const { selectedKpi } = useContext(MeasuresContext);
    const custIDs = selectedPlayers.map((p) => p.custId);

    // Group by make
    const [series, setSeries] = useState([]);
    const [status, setStatus] = useState(undefined);

    useEffect(() => {
        // Calculated weighted average by model
        setStatus("loading");
        setTimeout(() => {
            const dataSeries = Object.entries(filterSelectors).map(([make, modelsObject]) => {
                const lpAvgByModel = Object.values(modelsObject).map((versionArr) => {
                    const listPricesByVersion = versionArr.reduce((accLpVersion, versionObj) => {
                        const listPricesByCustIds = selectedPlayers.filter((p) => p.custId in versionObj && Number(versionObj[p.custId][1]) > 0).map((p) => versionObj[p.custId][1]);
                        accLpVersion[versionObj.label] = [...listPricesByCustIds];
                        return accLpVersion;
                    }, {});
                    const modeLpByVersion = Object.entries(listPricesByVersion).reduce((accLp, [key, arrLp]) => {
                        accLp[key] = Number(mode(arrLp)) > 0 ? Math.round(Number(mode(arrLp))) : null;
                        return accLp;
                    }, {});
                    return Object.values(modeLpByVersion).filter((val) => Number(val) > 0).length > 0 ? calculateAvg(Object.values(modeLpByVersion).filter((val) => Number(val) > 0)) : null;
                });
                const custIdValues = selectedPlayers.reduce((obj, player) => {
                    const val = calculationMode === "average" ? weightedAverageByMake(modelsObject, selectedKpi, calculationModes[calculationMode].calculationMethod, player.custId) : medianByMake(modelsObject, selectedKpi, calculationModes[calculationMode].calculationMethod, player.custId);
                    const roundedValue = ![null, 0].includes(val) ? selectedKpi.roundValue(val) : null;
                    return {
                        ...obj,
                        [player.custId]: roundedValue,
                    };
                }, {});

                return Object.assign({
                    name: make,
                    y: _.min(
                        Object.entries(custIdValues)
                            .filter(([key, val]) => custIDs.includes(key))
                            .map(([key, val]) => val),
                    ),
                    listPrice: lpAvgByModel.filter((val) => Number(val) > 0).length > 0 ? Math.round(calculateAvg(lpAvgByModel.filter((val) => Number(val) > 0))) : null,
                    ...custIdValues,
                });
            }, []);

            dataSeries?.sort((a, b) => b.y - a.y);
            const seriesTmp = [
                {
                    name: "Cheapest",
                    type: "column",
                    data: dataSeries,
                    legend: {
                        symbolWidth: 0,
                        symbolHeight: 0, // Hides the symbol for the column series
                    },
                    enableMouseTracking: false,
                    marker: {
                        enabled: false, // This completely removes the marker/symbol for the column series in the legend
                    },
                    color: "#7189AA",
                    // colorByPoint: true,
                    // legendColor: "#ff7d93",
                },
                ...selectedPlayers.map((player) =>
                    Object.assign({
                        name: [player.custLabel],
                        type: "spline",
                        marker: {
                            symbol: "circle",
                            radius: 5,
                            enabled: true,
                        },
                        color: player.custColor,
                        pointPlacement: showListPrices ? -0.1 : 0,
                        showInLegend: dataSeries.filter((serie) => serie[player.custId] !== null).length > 0,
                        data: dataSeries.map((serie) =>
                            Object.assign({
                                y: serie[player.custId],
                            }),
                        ),
                    }),
                ),
                {
                    name: "List price",
                    yAxis: 1,
                    maxPointWidth: 25,
                    color: "#FF7D93",
                    data: dataSeries.map((dt) => ({
                        //   ...dt,
                        y: dt.listPrice,
                    })),
                    events: {
                        legendItemClick: function () {
                            return false;
                        },
                    },
                    enableMouseTracking: false,
                    showInLegend: showListPrices,
                    visible: showListPrices,
                },
            ];
            setSeries([...seriesTmp]);
            setStatus("complete");
        }, 100);

        return () => {
            setSeries([]);
            setStatus(undefined);
        };
    }, [filterSelectors, selectedKpi.index, calculationMode, showListPrices]);

    return status === "loading" ? (
        <LoadingComponent />
    ) : (
        <div className="flex flex-col gap-2">
            <ColumnChart id={widgetId} filters={props.filters} series={series} calculationMode={calculationMode} selectedPlayers={selectedPlayers} menuButtonPosition={"inside"} showListPrices={showListPrices} {...props} />
            {props.footnote && <span className="pl-3 pb-2 text-left text-xs font-medium">{props.footnote}</span>}
        </div>
    );
}

/**
 *
 * Widget used on the dashboard
 */
const ColumnChartAnalysisWidget = ({ calculationMode = "average", ...props }) => {
    const { seriesBy, selectedKpi } = useContext(MeasuresContext);
    const { getCustlabelByCustId, getColorByCustId } = useAppContext();
    const [series, setSeries] = useState([]);
    const { status, result } = useExecutionDataView({
        execution: {
            seriesBy: seriesBy,
            slicesBy: [Md.CorrMake, Md.CorrModel, Md.CustId, Md.VersionStd],
            filters: props.filters,
        },
    });
    const playerFilter = props.filters.find((obj) => obj.positiveAttributeFilter.displayForm.identifier === Md.CustId.attribute.displayForm.identifier);

    const playerCustIDs = playerFilter?.positiveAttributeFilter.in.values.length > 0 ? playerFilter.positiveAttributeFilter.in.values : ["OEM", "CAP"];
    const selectedPlayers = playerCustIDs.map((custId) => ({
        custId: custId,
        custLabel: getCustlabelByCustId(custId),
        custColor: getColorByCustId(custId),
    }));

    const { getFirstFilterValue } = useContext(FiltersContext);
    const countryId = getFirstFilterValue("filterCountryId");
    const channel1 = getFirstFilterValue("filterChannel1");
    const custIDs = selectedPlayers.map((p) => p.custId);

    const makeModelObject = useMemo(() => {
        const slices = result?.data().slices().toArray();
        const res = slices ? transformSlicesToBrandTree(slices) : {};
        return res;
    }, [result, status, selectedKpi]);

    useEffect(() => {
        if (makeModelObject) {
            const dataSeries = Object.entries(makeModelObject).map(([make, modelsObject]) => {
                const custIdValues = selectedPlayers.reduce(
                    (obj, player) => {
                        const val = calculationMode === "average" ? weightedAverageByMake(modelsObject, selectedKpi, calculationModes[calculationMode].calculationMethod, player.custId) : medianByMake(modelsObject, selectedKpi, calculationModes[calculationMode].calculationMethod, player.custId);
                        const roundedValue = ![null, 0].includes(val) ? selectedKpi.roundValue(val) : null;
                        return {
                            ...obj,
                            [player.custId]: roundedValue,
                            listPrices: [
                                ...obj["listPrices"],
                                ...Object.values(modelsObject)
                                    .map((versionArr) => {
                                        const modeLp = mode(versionArr.filter((v) => v[player.custId] && v[player.custId].length > 0).map((v) => v[player.custId][1]));
                                        return Number(modeLp) > 0 ? Number(modeLp) : null;
                                    })
                                    .filter((val) => val > 0),
                            ],
                        };
                    },
                    { listPrices: [] },
                );

                return Object.assign({
                    name: make,
                    y: _.min(
                        Object.entries(custIdValues)
                            .filter(([key, val]) => custIDs.includes(key))
                            .map(([key, val]) => val),
                    ),
                    listPrice: Number(mode(custIdValues.listPrices)) > 0 ? Math.round(Number(mode(custIdValues.listPrices))) : null,
                    ...custIdValues,
                });
            }, []);

            dataSeries?.sort((a, b) => b.y - a.y);
            const seriesTmp = [
                {
                    name: "Cheapest",
                    type: "column",
                    data: dataSeries,
                    legend: {
                        symbolWidth: 0,
                        symbolHeight: 0, // Hides the symbol for the column series
                    },
                    enableMouseTracking: false,
                    marker: {
                        enabled: false, // This completely removes the marker/symbol for the column series in the legend
                    },
                    color: "#7189AA",
                    // colorByPoint: true,
                    // legendColor: "#ff7d93",
                },
                ...selectedPlayers.map((player) =>
                    Object.assign({
                        name: [player.custLabel],
                        type: "spline",
                        marker: {
                            symbol: "circle",
                            radius: 5,
                            enabled: true,
                        },
                        color: player.custColor,
                        pointPlacement: props.showListPrices ? -0.1 : 0,
                        showInLegend: dataSeries.filter((serie) => serie[player.custId] !== null).length > 0,
                        data: dataSeries.map((serie) =>
                            Object.assign({
                                y: serie[player.custId],
                            }),
                        ),
                    }),
                ),
                {
                    name: "List price",
                    yAxis: 1,
                    maxPointWidth: 25,
                    color: "#FF7D93",
                    data: dataSeries.map((dt) => ({
                        //   ...dt,
                        y: dt.listPrice,
                    })),
                    events: {
                        legendItemClick: function () {
                            return false;
                        },
                    },
                    enableMouseTracking: false,
                    showInLegend: props.showListPrices,
                    visible: props.showListPrices,
                },
            ];
            setSeries([...seriesTmp]);
        }
    }, [makeModelObject]);

    return status === "loading" || series.length <= 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 min-h-[400px]">
            <div>
                <LoadingComponent />
            </div>
            <span>Loading...</span>
        </div>
    ) : (
        <ColumnChart id={widgetId} filters={props.filters} series={series} calculationMode={calculationMode} selectedPlayers={selectedPlayers} {...props} />
    );
};

function ColumnChart({ id, filters, series, selectedPlayers = [], ...props }) {
    const categories = series.find((s) => s.name === "Cheapest")?.data.map((dt) => dt.name) || [];
    return (
        <HighchartsComponent
            widgetProps={{
                id,
                filters,
                section: "oem-views",
                calculationMode: props.calculationMode,
                showListPrices: props.showListPrices,
            }}
            options={{
                chart: {
                    type: "column",
                    height: 500,
                    marginTop: 65,
                    spacingBottom: 2,
                },
                plotOptions: {
                    column: {
                        grouping: true,
                        pointPadding: 0.05,
                        groupPadding: 0.3,
                        maxPointWidth: 60,
                        // colorByPoint: true,
                        dataLabels: {
                            enabled: true,
                        },
                        borderRadius: 3,
                        marker: {
                            symbol: "none", // This completely removes the marker/symbol for the column series in the legend
                        },
                        legendIndex: 0,
                        legend: {
                            symbolWidth: 0,
                            symbolHeight: 0, // Hides the symbol for the column series
                        },
                    },
                    spline: {
                        lineWidth: 0,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                },
                title: {
                    text: props.title,
                    align: "center",
                    style: {
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#3a0ca3",
                    },
                },
                subtitle: {
                    text: props.subtitle,
                    align: "center",
                    style: {
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#506e96",
                    },
                },
                xAxis: {
                    categories: categories,
                    // labels: labels || {
                    //     padding: 3,
                    //     reserveSpace: series.length > 0,
                    //     style: {
                    //         fontSize: pdfMode ? "8px" : "10px",
                    //         fontWeight: 600,
                    //         textOverflow: "none",
                    //     },
                    // },
                    title: {
                        text: null,
                    },
                },
                yAxis: [
                    {
                        // labels: {
                        //     enabled: false,
                        // },
                        title: {
                            text: null,
                        },
                        min: 0,
                        // max: series[0].y * 1.1,
                        // tickAmount: null,
                    },
                    {
                        title: {
                            text: null,
                        },
                        labels: {
                            enabled: false,
                        },
                        type: "logarithmic",
                        opposite: true,
                        tickInterval: 0.5,
                    },
                ],
                legend: {
                    symbolRadius: 1,
                    itemStyle: {
                        fontWeight: "bold",
                    },
                },
                series: series,
                lang: {
                    noData: "No data available",
                },
                noData: {
                    style: {
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#595959",
                    },
                },
            }}
            {...props}
        />
    );
}

export { ColumnChartAnalysis, ColumnChartAnalysisWidget };
